import ChessRestClient from '@/Api/ChessRestClient';
import RandomApiClient from '../../Api/RandomApiClient';
import {LoginResponse, SendOtpResponse} from './types';
import {useMutation, useQuery} from '@tanstack/react-query';
import {useState, useEffect} from 'react';
import {getLocalStorage, removeLocalStorage, setLocalStorage} from '../../Utils/CommonUtils';
import useChatRestClient from '@/Api/ChatRestClient';
import {useRoute} from '@react-navigation/native';
import {useUserPlanStore} from '@/Store/userPlanStore';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import useOneClickClient from '@/Api/oneClickClient';

export default function useLogin(
  handleOnSuccess?: (data: any) => void,
  handleOnError?: (data: any) => void,
) {
  const mutation = useMutation<
    LoginResponse,
    Error,
    {
      username: string;
      password: string;
      email: string;
      isLogin: boolean;
    }
  >(
    async loginData => {
      console.log('Calling Login / Registration API..');
      const {username, password, email, isLogin} = loginData;
      if (isLogin) {
        console.log('About to call login api ..');
        const response = await ChessRestClient.post('v1/login_api/', {
          username,
          password,
        });
        return response.data;
      } else {
        console.log('About to call register api ..');
        const response = await ChessRestClient.post('v1/register_api/', {
          username,
          password,
          email,
        });
        return response.data;
      }
    },
    {
      onSuccess: data => {
        if (handleOnSuccess && data.success) handleOnSuccess(data);
        else if (handleOnError) handleOnError(data);
      },
    },
  );

  return {
    ...mutation,
  };
}

// TODO: temp code, cleanup

export const useLoginState = reload => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const {setPlanDetails} = useUserPlanStore();
  const [error, setError] = useState('');
  const OneClickClient = useOneClickClient()
  const setUserData = useLoggedInUserStore(state => state.setUserData);
  useEffect(() => {
    const fetchData = async function () {
      try {
        setLoading(true);
        
        const extractOneClickTokenFromUrl = () => {
          try {
            const loc = window?.location;
            
            const url = new URL(window?.location.href);
            let token = url?.searchParams.get('token');

            // fallback: token in hash fragment (#token=...)
            if (!token && loc?.hash) {
              const hash = loc?.hash.replace(/^#/, '');
              const hp = new URLSearchParams(hash);
              token = hp.get('token') || null;
            }
            return token;
          } catch (e) {
            return null;
          }
        };

        // 1) If there's a one-click token in URL, attempt one-click login first
        const oneClickToken = extractOneClickTokenFromUrl();
        if (oneClickToken) {
          // remove token from URL right away to avoid leak/reuse
          try {
            const url = new URL(window?.location.href);
            url?.searchParams.delete('token');
            if (window?.location.hash) {
              const hash = window?.location?.hash?.replace(/^#/, '');
              const hp = new URLSearchParams(hash);
              if (hp.has('token')) {
                hp.delete('token');
                const newHash = hp.toString();
                url.hash = newHash ? `#${newHash}` : '';
              }
            }
            window?.history.replaceState({}, document.title, url.toString());
          } catch (e) {
            console.warn('Failed to clean token from URL', e);
          }


          // Call one-click login endpoint
          try {
            const oneClickRes = await OneClickClient.post('/auth/v1/oneClickLogin', {
              token: oneClickToken,
            });

            const apiData = oneClickRes?.data;

            // If JWT flow: save tokens (overwrite any previous tokens)
            if (apiData?.access_token && apiData?.refresh_token) {
              await setLocalStorage('refresh_token', apiData.refresh_token, true);
              await setLocalStorage('access_token', apiData.access_token, true);
              if (apiData?.user_key) {
                await setLocalStorage('userkey', apiData.user_key, true);
              }
              localStorage.setItem('loginEvent', Date.now());
              // proceed to dashboard fetch below
            } 
            else {
              // Unexpected payload: abort and treat as not logged in
              console.warn('oneClickLogin returned unexpected payload', apiData);
              setData({});
              setIsUserLoggedIn(false);
              return false;
            }
          } catch (err) {
            console.error('One-click login failed:', err?.response?.data || err.message || err);
            setData({});
            setIsUserLoggedIn(false);
            return false;
          }
        }


        const token = await getLocalStorage('access_token');
        console.log('useLoginState token: ', token);

        if (token) {
          const response = await ChessRestClient.get(
            'dashboard/v1/summary/?website=learning',
          );
          if (response.status === 200) {
            setData(response.data);
            setUserData(response.data.data.user);
            console.log('useLoginState response: ', response.data.data.user);
            const updateLocalStorage = async () => {
              try {
                await setLocalStorage(
                  'user_details',
                  JSON.stringify(response?.data?.data?.user),
                  true,
                );
                setPlanDetails?.(
                  response?.data?.data?.user_subscription_details,
                );
              } catch (error) {
                console.error('Failed to set local storage:', error);
                setError(`API request failed with status ${response.status}`);
              }
            };
            await updateLocalStorage();
            setIsUserLoggedIn(true);
          }
        } else {
          setData({});
          let currentPath = window?.location.pathname;
          if (currentPath?.startsWith('/')) {
            currentPath = currentPath?.slice(1);
          }
          console.log("currentPath", currentPath);
          await setLocalStorage('current_path',currentPath)
          setIsUserLoggedIn(false);
        }
      } catch (error) {
        setError('An unexpected error occurred. We will resolve it shortly.');
        throw error;
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reload]);

  return {loading, data, isUserLoggedIn, error};
};

export const sendOtp = (
  handleOnSuccess?: (data: any) => void,
  handleOnError?: (data: any) => void,
) => {
  const ChatRestClient = useChatRestClient();
  const mutation = useMutation<
    SendOtpResponse,
    Error,
    {
      mobile: string;
      countryCode: string;
      source: string;
      password?: string;
      admin_login?: boolean;
    }
  >(
    async loginData => {
      const {mobile, countryCode, password, admin_login} = loginData;
      const response = await ChatRestClient.post('auth/v1/send_otp', {
        mobile_number: mobile,
        country_code: countryCode,
        source: 'learn',
        password: password,
        admin_login: admin_login,
      });
      return response.data;
    },
    {
      onSettled: (data, error) => {
        if (handleOnSuccess && data) handleOnSuccess(data);
        else if (handleOnError && error) handleOnError(error);
      },
    },
  );

  return {
    ...mutation,
  };
};

export const verifyOtp = (
  handleOnSuccess?: (data: any) => void,
  handleOnError?: (data: any) => void,
) => {
  const ChatRestClient = useChatRestClient();
  const mutation = useMutation<
    SendOtpResponse,
    Error,
    {
      mobile: string;
      countryCode: string;
      otp: string;
      terms?: boolean;
      admin_login?: boolean;
    }
  >(
    async loginData => {
      const {mobile, countryCode, otp, terms, admin_login} = loginData;
      const referral_code = await getLocalStorage('referral_code');
      const response = await ChatRestClient.post('auth/v1/verify_otp', {
        mobile_number: mobile,
        country_code: countryCode,
        otp,
        sender: 'ccl',
        tnc: terms,
        source: 'learn',
        referral_code: referral_code ? referral_code : '',
        admin_login: admin_login,
      });
      return response.data;
    },
    {
      onSettled: (data, error) => {
        if (handleOnSuccess && data) handleOnSuccess(data);
        else if (handleOnError && error) handleOnError(error);
      },
    },
  );

  return {
    ...mutation,
  };
};

export const getTableauWorkbooks = (
  handleOnSuccess?: (data: any) => void,
  handleOnError?: (data: any) => void,
) => {
  const mutation = useMutation<any, Error>(
    async () => {
      const response = await ChessRestClient.get('learning/tableau_views/');
      return response.data;
    },
    {
      onSettled: (data, error) => {
        if (handleOnSuccess && data) handleOnSuccess(data);
        else if (handleOnError && error) handleOnError(error);
      },
    },
  );

  return {
    ...mutation,
  };
};
